import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  addresses: Address[];
  favorites: string[]; // IDs of favorite restaurants
}

export interface Address {
  id: string;
  label: string; // e.g. "Home", "Work", etc.
  street: string;
  city: string;
  state: string;
  postalCode: string;
  isDefault: boolean;
}

interface UserState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (userInfo: any) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  toggleFavorite: (restaurantId: string) => void;
  isSheetOpen: boolean;
  setIsSheetOpen: (isOpen: boolean) => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isSheetOpen: false,
      setIsSheetOpen: (isOpen: boolean) => set({ isSheetOpen: isOpen }),

      login: async (userInfo: string) => {
        set({ isLoading: true, isAuthenticated: true, error: null });

        try {
          // Simulating API call
          // await new Promise(resolve => setTimeout(resolve, 500));

          set({
            isAuthenticated: true,
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
        }
      },

      register: async (name, email, password) => {
        set({ isLoading: true, error: null });

        try {
          await new Promise(resolve => setTimeout(resolve, 500));

          set({
            user: {
              id: 'user-' + Date.now(),
              name,
              email,
              addresses: [],
              favorites: [],
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false,
          });
        }
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
        });
      },

      toggleFavorite: (restaurantId) => {
        const currentUser = get().user;
        if (!currentUser) return;

        const isFavorite = currentUser.favorites.includes(restaurantId);
        const updatedFavorites = isFavorite
          ? currentUser.favorites.filter(id => id !== restaurantId)
          : [...currentUser.favorites, restaurantId];

        set({
          user: {
            ...currentUser,
            favorites: updatedFavorites,
          },
        });
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
